import { useState, useRef, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { ALL_MACHINES, controlAPICall, localTime } from '@helpers';
import { getStatsRequestParams } from '@components/machines/components/eventsChart/helpers/period';
import type { TimePeriod } from '@v2/pages/machines/common/enums';

interface UseManageUtilizationStatsParams {
  id: string | number;
  selectedDate: string;
  selectedTime: TimePeriod;
  selectedTimeCounter: unknown;
  machineStatisticsOeeTeep: (params: Record<string, unknown>, signal?: AbortSignal) => Promise<{ machineStatistics?: Record<string, unknown>[] } | null>;
  machineStatisticsOeeTeepMachineList: (params: Record<string, unknown>, signal?: AbortSignal) => Promise<unknown>;
}

interface UseManageUtilizationStatsReturn {
  machineStatistics: Record<string, unknown>;
  loading: boolean;
  isError: boolean;
}

// v2 totals fetch + state. All periods, including eightWeeks, are supported by
// the whole-period endpoint.
export const useManageUtilizationStats = ({
  id,
  selectedDate,
  selectedTime,
  selectedTimeCounter,
  machineStatisticsOeeTeep,
  machineStatisticsOeeTeepMachineList,
}: UseManageUtilizationStatsParams): UseManageUtilizationStatsReturn => {
  const [machineStatistics, setMachineStatistics] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const abortController = useRef<AbortController>();
  const abortControllerMachine = useRef<AbortController>();

  useFocusEffect(
    useCallback(() => {
      const d = getStatsRequestParams(selectedTime, localTime(selectedDate));
      controlAPICall(machineStatisticsOeeTeepMachineList, d, abortControllerMachine);
    }, [selectedTime, selectedDate, selectedTimeCounter])
  );

  useFocusEffect(
    useCallback(() => {
      const loadData = async () => {
        setLoading(true);
        setIsError(false);

        const d: Record<string, unknown> = getStatsRequestParams(selectedTime, localTime(selectedDate));

        if (id !== ALL_MACHINES) {
          d.machineId = id;
        } else {
          d.forAllMachines = true;
        }

        const data = await controlAPICall(machineStatisticsOeeTeep, d, abortController);
        if (!data) {
          setLoading(false);
          return;
        }
        if (data?.machineStatistics?.[0]) {
          setMachineStatistics(data.machineStatistics[0]);
        } else {
          setIsError(true);
          setMachineStatistics({});
        }
        setLoading(false);
      };
      loadData();
    }, [selectedDate, selectedTime, id, selectedTimeCounter])
  );

  return { machineStatistics, loading, isError };
};
